import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg, fmtDate, openFile } from "@/lib/api";
import { PageHeader, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { DynamicField, ClassificationPicker, validateFields, sectionsOf } from "@/components/registration";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Upload, FileText } from "lucide-react";

const EDITABLE = ["draft", "additional_info_required"];

function clean(schema, data) {
  const keys = new Set(schema.fields.map((f) => f.key));
  return Object.fromEntries(Object.entries(data).filter(([k, v]) => keys.has(k) && v !== "" && v !== null && v !== undefined && v !== false));
}

function Documents({ app, schema, docs, reload, editable }) {
  const [busy, setBusy] = useState("");
  const upload = async (key, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("doc_type", key); fd.append("file", file);
    setBusy(key);
    try { await api.post(`/registration/applications/${app.id}/documents`, fd); toast.success("Uploaded"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3" data-testid="documents-step">
      <p className="text-sm text-slate-500">PDF, JPG or PNG up to 10 MB. Files are stored privately and only visible to you and authorized reviewers.</p>
      {schema.documents.map((d) => {
        const doc = docs.find((x) => x.doc_type === d.key);
        return (
          <div key={d.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-slate-200 rounded-lg p-4 bg-white">
            <div>
              <p className="font-medium text-sm">{d.label}{d.required ? <span className="text-rose-600"> *</span> : <span className="text-slate-400 text-xs"> (optional)</span>}</p>
              {doc ? <button className="text-xs text-amber-700 underline flex items-center gap-1 mt-1" onClick={() => openFile(doc.id)}><FileText className="h-3 w-3" />{doc.original_filename}</button>
                : <p className="text-xs text-slate-500 mt-1">{d.description || "Not uploaded"}</p>}
            </div>
            <div className="flex items-center gap-2">
              {doc && <StatusBadge status={doc.verification_status} />}
              {editable && (
                <label className={cn("inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border cursor-pointer hover:bg-slate-50", busy === d.key && "opacity-50")}>
                  <Upload className="h-4 w-4" />{doc ? "Replace" : "Upload"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid={`doc-upload-${d.key}`} onChange={(e) => upload(d.key, e.target.files[0])} />
                </label>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Review({ schema, data }) {
  return (
    <div className="space-y-5" data-testid="review-step">
      {sectionsOf(schema).map((s) => (
        <div key={s}>
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{s}</p>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {schema.fields.filter((f) => f.section === s && (data[f.key] !== undefined && data[f.key] !== "")).map((f) => (
              <div key={f.key} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
                <dt className="text-slate-500">{f.label}</dt>
                <dd className="font-medium text-right" data-testid={`review-${f.key}`}>{f.type === "checkbox" ? (data[f.key] ? "Yes" : "No") : f.type === "select" ? f.options.find((o) => o.value === data[f.key])?.label : data[f.key]}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

export default function ApplicationWizard() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [data, setData] = useState({});
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [schemas, setSchemas] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data: a } = await api.get(`/registration/applications/${id}`); setApp(a); return a; };
  useEffect(() => { load().then((a) => setData(a.data)); api.get("/registration/schemas").then((r) => setSchemas(r.data)); }, [id]); // eslint-disable-line
  const schema = app?.schema;
  const sections = useMemo(() => (schema ? sectionsOf(schema) : []), [schema]);
  if (!app) return null;
  const editable = EDITABLE.includes(app.status);
  const steps = ["Account type", ...sections, "Documents", "Review & submit"];
  const sectionFields = (i) => schema.fields.filter((f) => f.section === sections[i - 1]);

  const save = async () => {
    try { await api.put(`/registration/applications/${id}`, { data: clean(schema, data) }); return true; }
    catch (e) { const d = e.response?.data?.detail; if (d?.errors) setErrors(d.errors); toast.error(errMsg(e)); return false; }
  };
  const next = async () => {
    if (step >= 1 && step <= sections.length) {
      const errs = validateFields(sectionFields(step), data);
      setErrors(errs);
      if (Object.keys(errs).length) return toast.error("Please fix the highlighted fields");
      if (!(await save())) return;
    }
    setStep(step + 1);
  };
  const changeClass = async (c) => {
    if (c === app.classification) return;
    if (!window.confirm("Changing the account type removes fields and documents that do not apply to the new type. Continue?")) return;
    try {
      const { data: r } = await api.put(`/registration/applications/${id}/classification`, { classification: c });
      setData(r.data); setErrors({}); await load(); toast.success("Account type changed");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async () => {
    setBusy(true);
    const errs = validateFields(schema.fields, data);
    if (Object.keys(errs).length) {
      setErrors(errs); setBusy(false);
      const first = sections.findIndex((s) => schema.fields.some((f) => f.section === s && errs[f.key]));
      setStep(first + 1);
      return toast.error("Some required information is missing");
    }
    try {
      if (!(await save())) return;
      const { data: r } = await api.post(`/registration/applications/${id}/submit`);
      toast.success(r.status === "submitted" ? "Application submitted for review" : "Submitted — verify your email to continue");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow={app.reference_no} title={`${schema.label} registration`} description={schema.description}
        actions={<><StatusBadge status={app.status} testId="application-status" /><Button asChild variant="outline" size="sm"><Link to="/app/my-applications">Back</Link></Button></>} />
      {app.status_reason && app.status === "additional_info_required" && (
        <div className="mb-6 text-sm bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="reviewer-note">Reviewer note: {app.status_reason}</div>
      )}
      {!editable ? (
        <div className="grid lg:grid-cols-3 gap-6">
          <Panel title="Submitted information" className="lg:col-span-2"><Review schema={schema} data={app.data} />
            <div className="mt-6"><Documents app={app} schema={schema} docs={app.documents} reload={load} editable={false} /></div></Panel>
          <Panel title="Status history" testId="status-history">
            <ol className="space-y-3 text-sm">{app.history.map((h, i) => (
              <li key={i} className="border-l-2 border-amber-400 pl-3"><StatusBadge status={h.to} /><p className="text-xs text-slate-500 mt-1">{fmtDate(h.at)}{h.reason ? ` · ${h.reason}` : ""}</p></li>
            ))}</ol>
          </Panel>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          <ol className="space-y-1" data-testid="wizard-steps">
            {steps.map((s, i) => (
              <li key={s}><button onClick={() => i < step && setStep(i)} className={cn("w-full text-left text-sm px-3 py-2 rounded-md",
                i === step ? "bg-slate-900 text-white" : i < step ? "text-slate-700 hover:bg-slate-100" : "text-slate-400")} data-testid={`wizard-step-${i}`}>
                <span className="font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>{s}</button></li>
            ))}
          </ol>
          <Panel title={steps[step]} testId="wizard-panel">
            {step === 0 && (app.status === "draft" ? <ClassificationPicker schemas={schemas} value={app.classification} onChange={changeClass} />
              : <p className="text-sm">Account type: <b className="capitalize">{app.classification}</b> (locked after submission)</p>)}
            {step >= 1 && step <= sections.length && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid={`section-${sections[step - 1].toLowerCase().replace(/\s+/g, "-")}`}>
                {sectionFields(step).map((f) => (
                  <DynamicField key={f.key} field={f} data={data} value={data[f.key]} error={errors[f.key]}
                    onChange={(v) => { setData({ ...data, [f.key]: v }); setErrors({ ...errors, [f.key]: undefined }); }} />
                ))}
              </div>
            )}
            {step === sections.length + 1 && <Documents app={app} schema={schema} docs={app.documents} reload={load} editable />}
            {step === sections.length + 2 && <Review schema={schema} data={data} />}
            <div className="flex justify-between mt-8">
              <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back-button">Back</Button>
              {step < steps.length - 1 ? <Button onClick={next} data-testid="wizard-next-button">Save & continue</Button>
                : <Button onClick={submit} disabled={busy} data-testid="wizard-submit-button">Submit application</Button>}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
